var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { RestServiceBase } from "@ane/core/lib/services/RestServiceBase";
import { parseError } from "@ane/core/lib/utils/ErrorUtils";
import { buildOdataQuery } from "@ane/core/lib/utils/ODataUtils";
import { getUnifiedApiVersion } from "./ExpAPIVUnified.constants.js";
import EnvironmentService from "@ane/core/lib/services/EnvironmentService";
class UnifiedRestServiceBase extends RestServiceBase {
  /**
   * Default constructor.
   * @param resourceUrlSegment The url segment of the resource. For example: 'features' or 'assignment-units'. Should not include leading or trailing slashes.
   * @param apiVersion The api version to use. Defaults to the unified api version.
   */
  constructor(resourceUrlSegment, apiVersion = getUnifiedApiVersion(), isExperimentationGroupScoped = false) {
    super(EnvironmentService.endpoints.default, "/workspaces/", parseError);
    __publicField(this, "_resourceUrlSegment");
    __publicField(this, "_apiVersion");
    __publicField(this, "_isExperimentationGroupScoped");
    this._resourceUrlSegment = resourceUrlSegment;
    this._apiVersion = apiVersion;
    this._isExperimentationGroupScoped = isExperimentationGroupScoped;
  }
  getWorkspaceEgPath(workspaceId, egId) {
    return `${workspaceId}~${egId}`;
  }
  getWorkspaceEgUrl(workspaceId, egId) {
    return `${workspaceId}/experimentation-groups/${egId}`;
  }
  getWorkspaceIdAndEgFromPath(workspaceEgPath) {
    const [workspaceId, eg] = workspaceEgPath.split(new RegExp("~(.*)", "s"));
    return { workspaceId, eg };
  }
  /**
   * Gets the relative resource url for the given endpoint, optionally including the entity id.
   * @example /workspaces/{workspaceId}/features/{featureId}
   * @example /workspaces/{workspaceId}/features
   * @param workspaceId The workspace id.
   * @param entityId The entity id.
   * @param isEgScoped Whether the resource is scoped to an experimentation group.
   * @returns The relative resource url.
   */
  getResourceUrl(workspaceId, entityId, isEgScoped = false) {
    let baseUrl;
    if (this._isExperimentationGroupScoped || isEgScoped) {
      const { workspaceId: wsid, eg } = this.getWorkspaceIdAndEgFromPath(workspaceId);
      baseUrl = `${wsid}/experimentation-groups/${eg}/${this._resourceUrlSegment}`;
    } else {
      baseUrl = `${workspaceId}/${this._resourceUrlSegment}`;
    }
    return !!entityId ? `${baseUrl}/${entityId}` : baseUrl;
  }
  /**
   * Gets the default query params for a Unified API endpoint.
   * @returns The default query params.
   */
  getDefaultQueryParams() {
    return {
      "api-version": this._apiVersion
    };
  }
  /**
   * Queries for entities that are visible to the given experimentation group, i.e. belong
   * to the given experimentation group or an ancestor.
   * @param workspaceId The workspace id.
   * @param experimentationGroup The experimentation group to query for.
   * @param includeAncestors Whether to include entites that belong to ancestor experimentation groups.
   * @param odataPropertyName The experimentation group property name included in the OData filter expression.
   * @returns The paginated response.
   */
  getByExperimentationGroupAsync(workspaceId, experimentationGroup, includeAncestors = true, odataPropertyName = "ExperimentationGroup") {
    let filter = `${odataPropertyName} eq '${experimentationGroup}'`;
    if (includeAncestors) {
      filter += ` or startswith('${experimentationGroup}~', ${odataPropertyName})`;
    }
    return this._get(
      buildOdataQuery(
        {
          filter
        },
        {
          baseUrl: this.getResourceUrl(workspaceId),
          additionalQueryParams: this.getDefaultQueryParams()
        }
      )
    );
  }
  /**
   * Queries the given entities using the given filters.
   * @param workspaceId The workspace id.
   * @param query The odata query params.
   * @param additionalQueryParams Additional query params.
   * @returns The paginated results.
   */
  queryAsync(workspaceId, query, additionalQueryParams) {
    return this._get(
      buildOdataQuery(query, {
        baseUrl: this.getResourceUrl(workspaceId),
        cleanOdataDollarSignPrefix: true,
        additionalQueryParams: __spreadValues(__spreadValues({}, additionalQueryParams), this.getDefaultQueryParams())
      })
    );
  }
  /**
   * Gets an entity by id.
   * @param workspaceId The workspace id.
   * @param entityId The entity id.
   * @returns The entity.
   */
  getAsync(workspaceId, entityId) {
    return this._get(
      buildOdataQuery(
        {},
        {
          baseUrl: this.getResourceUrl(workspaceId, entityId),
          additionalQueryParams: this.getDefaultQueryParams()
        }
      )
    );
  }
  /**
   * Creates or updates a given entity.
   * @param workspaceId The workspace id.
   * @param entityId The entity id.
   * @param entity The entity.
   * @returns The created or updated entity.
   */
  createOrUpdateAsync(workspaceId, entityId, entity) {
    return this._put(
      entity,
      buildOdataQuery(
        {},
        {
          baseUrl: this.getResourceUrl(workspaceId, entityId),
          additionalQueryParams: this.getDefaultQueryParams()
        }
      )
    );
  }
  /**
   * Deletes a given entity by id.
   * @param workspaceId The workspace id.
   * @param entityId The entity id.
   * @returns The empty rest result.
   */
  deleteAsync(workspaceId, entityId) {
    return this._delete(
      buildOdataQuery(
        {},
        {
          baseUrl: this.getResourceUrl(workspaceId, entityId),
          additionalQueryParams: this.getDefaultQueryParams()
        }
      )
    );
  }
}
export {
  UnifiedRestServiceBase
};
//# sourceMappingURL=ExpAPIVUnified.resource.js.map
